/**
 * Shared dashboard layout primitives + table helpers.
 *
 * The Subscriptions dashboard, Upcoming charges dashboard and Invoices list
 * all use the same surface — same header bar, search input, filter pills,
 * cell typography, status/type tags. Importing from here keeps them visually
 * consistent and makes a future style change a single-file edit.
 */

import styled from "styled-components";
import { DateTime } from "luxon";

// ─────────────────────────────────────────────────────────────────────────────
// Layout
// ─────────────────────────────────────────────────────────────────────────────

export const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  box-sizing: border-box;
  /* Bound the content width to the container so wide tables trigger
     horizontal scroll inside ResponsiveWrapper instead of pushing the
     container beyond its parent. */
  width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 30px 20px;
  @media (max-width: 900px) {
    padding: 12px;
  }
`;

export const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 10px;
  gap: 12px;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TableHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const TableHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ResponsiveWrapper = styled.div`
  /* Scroll horizontally if the table can't fit the container —
     otherwise wide rightmost columns spill out of TableContainer's padding
     box. min-width: 0 lets the wrapper shrink inside flex parents so the
     scroll actually engages instead of pushing the parent wider.
     Not a scroll container on mobile: the table renders as cards there,
     and a nested scroller breaks vertical scroll chaining in WebKit. */
  width: 100%;
  min-width: 0;
  @media (min-width: 768px) {
    overflow-x: auto;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Form bits used by table headers
// ─────────────────────────────────────────────────────────────────────────────

export const SearchInput = styled.input`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  padding: 8px 14px;
  border: 1px solid #dbe3eb;
  border-radius: 6px;
  outline: none;
  width: 260px;
  &:focus {
    border-color: #027aff;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const FilterPill = styled.button<{ $active?: boolean; $color?: string }>`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.$active ? p.$color || "#027AFF" : "#dbe3eb")};
  background: ${(p) => (p.$active ? (p.$color || "#027AFF") + "14" : "#fff")};
  color: ${(p) => (p.$active ? p.$color || "#027AFF" : "#495b6c")};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover {
    border-color: ${(p) => p.$color || "#027AFF"};
    background: ${(p) => (p.$color || "#027AFF") + "0a"};
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Cell / tag primitives — used inside ReactTableComponent column `cell`
// renderers so all three tabs have the same in-cell typography and tags.
// ─────────────────────────────────────────────────────────────────────────────

export const Cell = styled.div`
  padding: 10px 6px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
`;

export const SubTag = styled.span<{
  $variant?: "active" | "trial" | "failed" | "warning" | "default";
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $variant }) =>
    $variant === "active"
      ? "rgba(44, 209, 158, 0.17)"
      : $variant === "trial"
      ? "rgba(249, 160, 0, 0.17)"
      : $variant === "failed"
      ? "rgba(239, 99, 85, 0.17)"
      : $variant === "warning"
      ? "rgba(249, 160, 0, 0.17)"
      : "#F4F5F9"};
  color: ${({ $variant }) =>
    $variant === "active"
      ? "#00bc82"
      : $variant === "trial"
      ? "#f9a000"
      : $variant === "failed"
      ? "#ef6355"
      : $variant === "warning"
      ? "#f9a000"
      : "#495b6c"};
`;

export const TypeTag = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background: ${(p) => p.$bg};
`;

export const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #8181a5;
`;

export const DateCell = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #13273f;
  line-height: 1.3;
`;

export const DateSub = styled.span<{ $urgent?: boolean }>`
  font-size: 11px;
  color: ${(p) => (p.$urgent ? "#ef6355" : "#8181a5")};
  font-weight: ${(p) => (p.$urgent ? 600 : 400)};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

export const formatPence = (pence: number): string =>
  `£${(pence / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const formatPenceShort = (pence: number): string =>
  pence >= 100_000 ? `£${(pence / 100_000).toFixed(1)}k` : formatPence(pence);

export const getStatusVariant = (
  status: string | null | undefined
): {
  variant: "active" | "trial" | "failed" | "warning" | "default";
  label: string;
} => {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return { variant: "active", label: "Active" };
    case "trialing":
      return { variant: "trial", label: "Trial" };
    case "payment_failed":
    case "failed":
      return { variant: "failed", label: "Payment Failed" };
    case "payment_action_required":
      return { variant: "warning", label: "Action Required" };
    case "past_due":
      return { variant: "warning", label: "Past Due" };
    case "incomplete":
      return { variant: "default", label: "Incomplete" };
    case "paused":
      return { variant: "default", label: "Paused" };
    case "canceled":
    case "cancelled":
      return { variant: "failed", label: "Cancelled" };
    case "paid":
      return { variant: "active", label: "Paid" };
    case "pending":
      return { variant: "warning", label: "Pending" };
    case "void":
      return { variant: "default", label: "Voided" };
    default:
      return { variant: "default", label: status ?? "—" };
  }
};

export const getTypeColor = (option: string | null | undefined): string => {
  switch (option) {
    case "PAYMENTS_ONLY":
      return "#13273F";
    case "TOOLKIT":
      return "#027AFF";
    case "TOOLKIT_MOBILE":
      return "#2CD19E";
    default:
      return "#8181a5";
  }
};

export const getTypeLabel = (option: string | null | undefined): string => {
  switch (option) {
    case "PAYMENTS_ONLY":
      return "Payments Only";
    case "TOOLKIT":
      return "Toolkit";
    case "TOOLKIT_MOBILE":
      return "Mobile";
    default:
      return option ?? "—";
  }
};

export const formatUpcomingDate = (
  dateStr: string | null | undefined
): { formatted: string; sub: string; urgent: boolean } => {
  if (!dateStr) return { formatted: "—", sub: "", urgent: false };
  const dt = DateTime.fromISO(dateStr);
  if (!dt.isValid) return { formatted: "—", sub: "", urgent: false };
  const now = DateTime.now();
  const diffDays = Math.ceil(dt.diff(now, "days").days);
  const formatted = dt.toFormat("dd MMM yyyy");
  if (diffDays < 0)
    return { formatted, sub: `${Math.abs(diffDays)}d overdue`, urgent: true };
  if (diffDays === 0) return { formatted, sub: "today", urgent: true };
  if (diffDays === 1) return { formatted, sub: "tomorrow", urgent: true };
  if (diffDays <= 3) return { formatted, sub: `in ${diffDays}d`, urgent: true };
  return { formatted, sub: `in ${diffDays}d`, urgent: false };
};
